// Create a BLE lamp PARENT or GROUP — /ble-lamp-group/new?locationId=&roomId=&parentNodeId=
//
// Both are DeviceID 100 devices shown in the room; a group holds lamps, a
// parent holds groups ("All WWH Lights" → "Left Section" + "Right Section").
// Creating one needs no hardware: the server allocates the unique number
// (BL-L<loc>-R<room>-P1 / -G1) and maps the device to this user. Reached from
// the room screen's circle menu, or from a parent's "New group".
//
// The "phone app" choice decides the radio family for the whole tree:
// LampSmart / FanLamp (advertising) or Bluetooth Mesh (Telink, Codec
// 'sig_mesh'). A group may only sit under a parent of the same family — the
// server refuses a mix — so the parent list is filtered to match.

import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, TextField, Typography, useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useMessage } from '../components/MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import { getRooms } from '../../../core/api/api';
import { addBleNode, getBleNodesAtLocation } from '../../../core/api/bleNodes';
import { LAMP_SMART, FAN_LAMP, primaryCodecsForApp } from '../../../core/ble/lamp/codecs';
import { MESH_CODEC, isMeshCodec } from '../../../core/ble/mesh/meshValues';
import { isNativePlatform } from '../../../core/platform';

const MESH = 'mesh';

export default function BleNodeSetupScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userId } = useApp();
  const { show } = useMessage();

  const params = new URLSearchParams(search);
  const locationId = Number(params.get('locationId')) || 0;
  const presetParent = params.get('parentNodeId') || '';
  const [nodeType, setNodeType] = useState('group');
  const [roomId, setRoomId] = useState(params.get('roomId') || '');
  const [parentId, setParentId] = useState(presetParent);
  const [rooms, setRooms] = useState([]);
  const [parents, setParents] = useState([]);
  const [name, setName] = useState('');
  const [app, setApp] = useState(LAMP_SMART);
  const [busy, setBusy] = useState(false);
  const presetParentNode = parents.find((p) => String(p.NodeId) === String(presetParent));
  // Coming from a parent's "New group": the family is the parent's.
  useEffect(() => {
    if (presetParentNode && isMeshCodec(presetParentNode.Codec)) setApp(MESH);
  }, [presetParentNode]);
  const sameFamily = parents.filter((p) => isMeshCodec(p.Codec) === (app === MESH));

  useEffect(() => {
    if (!userId || !locationId) return undefined;
    let alive = true;
    getRooms(userId, locationId)
      .then((res) => { if (alive) setRooms(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    getBleNodesAtLocation(userId, locationId, 'parent')
      .then((res) => { if (alive) setParents(res.nodes || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [userId, locationId]);

  const submit = async () => {
    if (busy) return;
    if (!locationId) { show('No location', 'Open a room first, then add from there.'); return; }
    if (!name.trim()) { show('Name needed', nodeType === 'parent' ? 'e.g. "All WWH Lights"' : 'e.g. "Left Section WWH Lights"'); return; }
    setBusy(true);
    try {
      const res = await addBleNode(userId, {
        nodeType,
        name: name.trim(),
        locationId,
        roomId: Number(roomId) || 0,
        parentNodeId: nodeType === 'group' && parentId ? Number(parentId) : null,
        codec: app === MESH ? MESH_CODEC : primaryCodecsForApp(app)[0].id,
      });
      navigate(`/device/${res.node.UniqueNumber}`, { replace: true, state: { locationId, roomId: Number(roomId) || 0 } });
    } catch (e) {
      show("Couldn't create it", e?.message || 'Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title={nodeType === 'parent' ? 'New BLE Lamp Parent' : 'New BLE Lamp Group'} showBack />
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, maxWidth: 480, mx: 'auto', width: '100%' }}>
        <Typography sx={{ color: c.text, fontSize: 15, mb: 2 }}>
          A <b>group</b> holds the lamps you pair (e.g. Left Section WWH Lights). A <b>parent</b> holds groups and controls all of them at once (e.g. All WWH Lights). Lamps are added to a group after it exists.
        </Typography>
        {!isNativePlatform() && (
          <Typography sx={{ color: `${c.text}99`, fontSize: 13, mb: 2 }}>
            You can create these here, but pairing and controlling lamps needs the Android app&apos;s Bluetooth.
          </Typography>
        )}

        <TextField select fullWidth label="What to create" value={nodeType} onChange={(e) => setNodeType(e.target.value)} SelectProps={{ native: true }} sx={{ mb: 2 }}>
          <option value="group">Group of lamps</option>
          <option value="parent">Parent (controls several groups)</option>
        </TextField>

        <TextField
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={nodeType === 'parent' ? 'All WWH Lights' : 'Left Section WWH Lights'}
          sx={{ mb: 2 }}
        />
        <TextField select fullWidth label="Room" value={roomId} onChange={(e) => setRoomId(e.target.value)} SelectProps={{ native: true }} sx={{ mb: 2 }}>
          <option value="">No room (common device)</option>
          {rooms.map((r) => <option key={r.RoomID} value={String(r.RoomID)}>{r.RoomName}</option>)}
        </TextField>

        {nodeType === 'group' && (
          <TextField select fullWidth label="Inside a parent?" value={parentId} onChange={(e) => setParentId(e.target.value)} SelectProps={{ native: true }} sx={{ mb: 2 }} helperText="Optional. A parent's commands reach this group's lamps too. Can be changed later.">
            <option value="">No parent</option>
            {sameFamily.map((p) => <option key={p.NodeId} value={String(p.NodeId)}>{p.Name}</option>)}
          </TextField>
        )}

        <TextField select fullWidth label="Which phone app came with the lamps?" value={app} onChange={(e) => { setApp(e.target.value); setParentId(''); }} SelectProps={{ native: true }} helperText={app === MESH ? 'Telink SIG Mesh drivers. Lamps are paired from the group with the phone near them; this cannot be switched to LampSmart later.' : "Printed on the lamp's box or manual. Decides the radio encoding; it can be changed later."} sx={{ mb: 3 }}>
          <option value={LAMP_SMART}>{LAMP_SMART}</option>
          <option value={FAN_LAMP}>{FAN_LAMP}</option>
          <option value={MESH}>Bluetooth Mesh (Telink) — white / warm / cool</option>
        </TextField>

        <Button fullWidth variant="contained" disabled={busy} onClick={submit} sx={{ bgcolor: c.button, color: c.text, borderRadius: '12px', py: 1.5, '&:hover': { bgcolor: c.button } }}>
          {busy ? <CircularProgress size={22} sx={{ color: c.text }} /> : (nodeType === 'parent' ? 'Create parent' : 'Create group')}
        </Button>
      </Box>
    </Box>
  );
}
